import { normalizeEmail } from "../../../lib/utils";
import { getUserLabel } from "../../../lib/identity";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../../../lib/anonymous-voter-identities";

// D4 — gate identity re-resolution by display mode.
//
// SchedulerPage keeps a `participantMapByEmail` of the REAL participants and
// historically re-resolved every displayed voter through it
// (`participantMapByEmail.get(normalizeEmail(voter.email)) || voter`). Under
// anonymization that was only safe by coincidence: anonymized voters carry a
// synthetic `anon:` email that misses the map, so the fallback returned the
// (already anonymized) voter. Make the gate EXPLICIT: in "anonymous" or
// "hidden" mode never re-resolve — return the given (already-anonymized /
// placeholder) object as-is, so a future caller that passes a real email here
// can never accidentally surface the real participant. In "named" mode the
// re-resolution is desired (it enriches a voter stub with the full profile).
export function resolveVoterForDisplay(userInfo, participantMapByEmail, mode) {
  if (!userInfo?.email) return userInfo;
  if (mode === "anonymous" || mode === "hidden") return userInfo;
  if (!(participantMapByEmail instanceof Map)) return userInfo;
  return participantMapByEmail.get(normalizeEmail(userInfo.email)) || userInfo;
}

// B1 §3 — email-privacy guardrail for the per-slot voter identity cards.
//
// `resolveVoterForDisplay` enriches a voter STUB against the participant map. A
// participant resolves to a full profile carrying publicIdentifier/
// publicIdentifierType, so <UserIdentity> renders their CHOSEN identifier.
//
// Locked identity semantics: publicIdentifierType "qsUsername" /
// "discordUsername" is an EXPLICIT non-email choice (withhold the email); ANY
// absent/null type is the DEFAULT EMAIL (the email is theirs to show). So the
// ONLY thing this wrapper must do is, for an EXPLICIT non-email voter, drop the
// raw email as belt-and-suspenders — getUserIdentity/buildPublicIdentifier
// already never surface an explicit non-email voter's email, but stripping it
// here guarantees no downstream render/serialization can leak it. The chosen
// handle fields are KEPT so <UserIdentity> still renders the handle. Email-type
// and untyped (default-email) voters are returned untouched (email preserved).
export function resolveVoterIdentityForDisplay(userInfo, participantMapByEmail, mode) {
  const resolved = resolveVoterForDisplay(userInfo, participantMapByEmail, mode);
  if (!resolved) return resolved;
  const type = resolved.publicIdentifierType;
  const isExplicitNonEmail = type === "qsUsername" || type === "discordUsername";
  // Email-type + untyped (default-email): show as-is.
  if (!isExplicitNonEmail) return resolved;
  // Explicit non-email: belt-and-suspenders drop the raw email; KEEP handle
  // fields so <UserIdentity> renders the chosen handle.
  return { ...resolved, email: undefined };
}

// B1 §3 — neutral fallback for the required-attendance title. The old inline
// `getUserLabel(participant) || email` fell back to the raw email when a
// participant had no displayable label (e.g. they chose a non-email identifier
// but never set its handle, so getUserLabel returns ""). The fallback must be a
// neutral placeholder, NEVER the email.
export function safeParticipantLabel(participant, fallback = "Participant") {
  // getUserLabel is strict: it returns an email only for email-type users.
  // The neutral fallback (never the raw email) covers the empty-label case.
  return getUserLabel(participant) || fallback;
}

// R14 — creator-edit slot voter anonymization.
//
// CreateSchedulerPage's creator-edit view builds per-slot Preferred/Feasible
// voter stacks from REAL vote docs (real emails/avatars). Under
// `all_participants` anonymization the creator must see ALIASES, not real
// names (the creator is never hidden — R14 — only aliased). This anonymizes the
// slotVoters-by-slotId map in "anonymous" mode (self-exempting the creator via
// `currentUser`) and returns it unchanged in any other mode (creator is never
// "hidden", so there is no placeholder path here).
export function anonymizeSlotVotersForCreator(
  slotVotersBySlotId,
  { mode, currentUser = null, scopeKey = "creator-edit" } = {}
) {
  if (mode !== "anonymous" || !slotVotersBySlotId) return slotVotersBySlotId;
  const seed = [];
  Object.values(slotVotersBySlotId).forEach((slotVoteUsers) => {
    seed.push(...(slotVoteUsers?.preferred || []));
    seed.push(...(slotVoteUsers?.feasible || []));
  });
  const identityMap = buildAnonymousIdentityMap(seed, { scopeKey });
  const out = {};
  Object.entries(slotVotersBySlotId).forEach(([slotId, slotVoteUsers]) => {
    out[slotId] = {
      preferred: anonymizeUsers(slotVoteUsers?.preferred || [], identityMap, {
        keyPrefix: `${slotId}-preferred`,
        currentUser,
      }),
      feasible: anonymizeUsers(slotVoteUsers?.feasible || [], identityMap, {
        keyPrefix: `${slotId}-feasible`,
        currentUser,
      }),
    };
  });
  return out;
}
